/**
 * Generates temporary passwords for the admin "Add User" flow.
 *
 * Kept next to `passwordValidation.ts` on purpose: the character sets below mirror
 * the policy that `validatePassword` enforces, and a generated password always
 * satisfies it. If the policy changes, both files change together.
 */

const LOWERCASE = 'abcdefghijkmnopqrstuvwxyz'; // no 'l'
const UPPERCASE = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // no 'I', 'O'
const NUMBERS = '23456789'; // no '0', '1'
const SPECIALS = '!@#$%^&*()_+-=[]{}|;:,.<>?';

const ALL = LOWERCASE + UPPERCASE + NUMBERS + SPECIALS;

/** Cryptographically secure index into `chars`, without modulo bias. */
const randomChar = (chars: string): string => {
  const limit = Math.floor(0xffffffff / chars.length) * chars.length;
  const buf = new Uint32Array(1);
  let value: number;
  do {
    crypto.getRandomValues(buf);
    value = buf[0];
  } while (value >= limit);
  return chars[value % chars.length];
};

/** Fisher-Yates using the same secure source, so position isn't predictable. */
const shuffle = (chars: string[]): string[] => {
  for (let i = chars.length - 1; i > 0; i--) {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    const j = buf[0] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars;
};

/**
 * Generate a password that always passes `validatePassword`.
 *
 * One character is drawn from each required set first so the policy can never be
 * missed by chance, then the remainder is filled from the full alphabet and the
 * whole thing shuffled so the guaranteed characters aren't always in front.
 *
 * Visually ambiguous characters (0/O, 1/l/I) are excluded — this password gets
 * read off a screen and typed by hand, so they cause real support noise.
 */
export const generatePassword = (length: number = 16): string => {
  const required = [
    randomChar(LOWERCASE),
    randomChar(UPPERCASE),
    randomChar(NUMBERS),
    randomChar(SPECIALS),
  ];

  const size = Math.max(length, required.length);
  const rest = Array.from({ length: size - required.length }, () => randomChar(ALL));

  return shuffle([...required, ...rest]).join('');
};
